import { AlertTriangle, Clock, ExternalLink } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useLanguage } from "@/hooks/useLanguage";
import {
  FIO_IB_URL,
  TOKEN_MAX_DAYS,
  TOKEN_WARN_DAYS,
  tokenDaysLeft,
  tokenHealth,
  type FioTokenAccount,
} from "@/lib/fio/tokenHealth";

/**
 * How to renew a Fio token, from Fio's API documentation
 * (API_Bankovnictvi.pdf v1.9, §2 "Vlastnosti tokenu").
 */
export function FioTokenHelp() {
  const { language } = useLanguage();
  const cs = language === "cs";
  const ibUrl = cs ? FIO_IB_URL : `${FIO_IB_URL}?l=ENGLISH`;
  return (
    <details className="mt-2 text-sm">
      <summary className="cursor-pointer font-medium text-foreground">
        {cs ? "Jak obnovit token" : "How to renew the token"}
      </summary>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-muted-foreground">
        <li>
          {cs ? "Přihlaste se do " : "Sign in to "}
          <a
            href={ibUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
          >
            {cs ? "Fio internetbankingu" : "Fio Internetbanking"}
            <ExternalLink className="h-3 w-3" />
          </a>
          .
        </li>
        <li>
          {cs
            ? "Vpravo nahoře otevřete Nastavení a záložku API, a přidejte nový token k účtu."
            : "Open Settings (top right), the API tab, and add a new token for the account."}
        </li>
        <li>
          {cs
            ? `Zvolte práva „Sledování účtu“, platnost ${TOKEN_MAX_DAYS} dní a zapněte automatické prodlužování.`
            : `Choose the "account monitoring" right, ${TOKEN_MAX_DAYS} days of validity, and switch on automatic renewal.`}
        </li>
        <li>
          {cs
            ? "Nový token vložte sem k účtu a uložte."
            : "Paste the new token into the account here and save."}
        </li>
      </ol>
      <p className="mt-2 text-xs text-muted-foreground">
        {cs
          ? `S automatickým prodlužováním Fio prodlouží platnost na ${TOKEN_MAX_DAYS} dní při každém přihlášení do internetbankingu nebo Smartbankingu. Stačí se tedy přihlásit aspoň jednou za ${TOKEN_MAX_DAYS} dní.`
          : `With automatic renewal, Fio extends the token to ${TOKEN_MAX_DAYS} days each time you sign in to Internetbanking or Smartbanking, so signing in once every ${TOKEN_MAX_DAYS} days is enough.`}
      </p>
    </details>
  );
}

/**
 * The Fio token warnings for Banky a integrace: tokens Fio has refused for
 * 20+ hours (firm), refused just now (soft: it may be a Fio outage), and tokens
 * saved TOKEN_WARN_DAYS+ days ago. Nothing when every token is fine.
 */
export function FioTokenAlert({
  accounts,
  now = new Date(),
}: {
  accounts: FioTokenAccount[];
  now?: Date;
}) {
  const { language } = useLanguage();
  const cs = language === "cs";
  const inactive = accounts.filter((a) => tokenHealth(a, now) === "inactive");
  const suspected = accounts.filter((a) => tokenHealth(a, now) === "inactive_suspected");
  const expiring = accounts.filter((a) => tokenHealth(a, now) === "expiring");
  if (!inactive.length && !suspected.length && !expiring.length) return null;

  return (
    <div className="space-y-3">
      {inactive.length > 0 && (
        <Alert variant="destructive" data-testid="fio-token-inactive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {cs ? "Fio token přestal platit" : "Fio stopped accepting the token"}
          </AlertTitle>
          <AlertDescription>
            {cs
              ? "Fio odmítlo token (vypršel nebo byl zrušen), proto se nové platby nenačítají u účtu: "
              : "Fio refused the token (it expired or was revoked), so new payments are not loading for: "}
            <span className="font-medium">{inactive.map((a) => a.name).join(", ")}</span>
            <FioTokenHelp />
          </AlertDescription>
        </Alert>
      )}
      {suspected.length > 0 && (
        <Alert data-testid="fio-token-suspected">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {cs ? "Fio neodpovědělo na token" : "Fio did not accept the token"}
          </AlertTitle>
          <AlertDescription>
            {cs
              ? "Fio token možná vypršel, nebo je Fio API dočasně nedostupné. Pokud to přetrvá, obnovte token. Týká se účtu: "
              : "The Fio token may have expired, or the Fio API is temporarily unavailable. If it persists, renew the token. Account: "}
            <span className="font-medium">{suspected.map((a) => a.name).join(", ")}</span>
            <FioTokenHelp />
          </AlertDescription>
        </Alert>
      )}
      {expiring.length > 0 && (
        <Alert data-testid="fio-token-expiring">
          <Clock className="h-4 w-4" />
          <AlertTitle>{cs ? "Fio token brzy vyprší" : "A Fio token expires soon"}</AlertTitle>
          <AlertDescription>
            <ul className="mt-1 space-y-0.5">
              {expiring.map((a) => {
                const left = tokenDaysLeft(a.token_saved_at, now) ?? 0;
                return (
                  <li key={a.id}>
                    <span className="font-medium">{a.name}</span>
                    {": "}
                    {cs
                      ? `token je uložený přes ${TOKEN_WARN_DAYS} dní, bez automatického prodlužování přestane platit za ${left} dní.`
                      : `the token was saved over ${TOKEN_WARN_DAYS} days ago; without automatic renewal it stops working in ${left} days.`}
                  </li>
                );
              })}
            </ul>
            <FioTokenHelp />
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
